import { Injectable } from '@angular/core';
import isEqual from 'lodash/isEqual';
import { Observable, Subject } from 'rxjs';
import { EDashletType } from 'src/app/project/modules/dashboard/dashlets-enums';
import { TDashletBudgetItem } from 'src/app/project/modules/dashboard/new-dashlet-modal/new-dashlet-modal-cost-fields/new-dashlet-modal-cost-fields.consts';
import { TDashlet, TDashletSiteRange } from 'src/app/project/modules/preferences/preferences.model';
import { TAsset } from '../../../../asset-service/asset.consts';
import { AssetService } from '../../../../asset-service/asset.service';
import { generateNewAssetDashlet } from './utils/generate-new-asset-dashlet';

@Injectable({
  providedIn: 'root',
})
export class AssetDashboardService {
  private readonly _expandedAssetIdChange = new Subject<string>();
  readonly expandedAssetIdChange$ = this._expandedAssetIdChange.asObservable();

  private expandedAssetId: string;

  constructor(private assetService: AssetService) {}

  setExpandedAssetId(assetId: string): void {
    this.expandedAssetId = assetId;

    this._expandedAssetIdChange.next(assetId);
  }

  getExpandedAssetId(): string {
    return this.expandedAssetId;
  }

  addDashlet(field: { value: EDashletType }, assetId: string): void {
    const asset = this.assetService.getAsset(assetId);
    const newDashlet = generateNewAssetDashlet(field.value);

    asset.dashlets.push(newDashlet);

    this.assetService
      .editAsset(assetId, {
        dashlets: asset.dashlets,
      })
      .subscribe();
  }

  addCostComparisonDashlet(assetId: string, data: TDashletBudgetItem[], customName: string): void {
    const asset = this.assetService.getAsset(assetId);
    const newDashlet = generateNewAssetDashlet(EDashletType.COST_COMPARISON);

    newDashlet.selectedRange.workspacesCosts = {};
    newDashlet.customName = customName;

    data.forEach((item) => {
      newDashlet.selectedRange.workspacesCosts[item.workspaceId] = [
        item.budgetFieldId,
        item.costFieldId,
      ];
    });

    asset.dashlets.push(newDashlet);

    this.assetService
      .editAsset(assetId, {
        dashlets: asset.dashlets,
      })
      .subscribe();
  }

  addStackedStatusDashlet(assetId: string): void {
    const asset = this.assetService.getAsset(assetId);
    const newDashlet = generateNewAssetDashlet(EDashletType.CURRENT_STACKED_STATUS);

    const items: string[] = [];

    for (const site of asset.sites) {
      if (items.length < 5) {
        items.push(site.workspaceId);
      } else {
        break;
      }
    }

    newDashlet.selectedRange.workspaceIds = items;

    asset.dashlets.push(newDashlet);

    this.assetService
      .editAsset(assetId, {
        dashlets: asset.dashlets,
      })
      .subscribe();
  }

  addValueBreakdownDashlet(
    assetId: string,
    selectedRange: TDashletSiteRange,
    customName: string,
  ): void {
    const asset = this.assetService.getAsset(assetId);
    const newDashlet = generateNewAssetDashlet(EDashletType.VALUE_BREAKDOWN);

    newDashlet.selectedRange = selectedRange;
    newDashlet.customName = customName;

    asset.dashlets.push(newDashlet);

    this.assetService
      .editAsset(assetId, {
        dashlets: asset.dashlets,
      })
      .subscribe();
  }

  removeDashlet(assetId: string, dashlet: TDashlet): Observable<TAsset> {
    const asset = this.assetService.getAsset(assetId);
    const dashlets = asset.dashlets;

    const dashletIndex = dashlets.findIndex(
      (assetDashlet) =>
        assetDashlet.name === dashlet.name &&
        dashlet.x === assetDashlet.x &&
        dashlet.y === assetDashlet.y &&
        dashlet.period === assetDashlet.period &&
        dashlet.selectedRange.accountId === assetDashlet.selectedRange.accountId &&
        isEqual(dashlet.selectedRange.workspaceIds, assetDashlet.selectedRange.workspaceIds) &&
        dashlet.cols === assetDashlet.cols &&
        dashlet.rows === assetDashlet.rows,
    );

    dashlets.splice(dashletIndex, 1);

    return this.assetService.editAsset(assetId, {
      dashlets: dashlets,
    });
  }

  updateAssetDashboard(dashlets: TDashlet[], assetId: string): void {
    const currentDashlets = this.assetService.getAsset(assetId).dashlets;
    const assetsEqual = isEqual(currentDashlets, dashlets);

    if (assetsEqual) {
      return;
    }

    this.assetService
      .editAsset(assetId, {
        dashlets: dashlets,
      })
      .subscribe();
  }
}
